export const REALTIME_PATH = "/api/realtime";
export const REALTIME_PROTOCOL_VERSION = 1;
export const REALTIME_ORIGIN_HEADER = "X-Family-Realtime-Client";

export const REALTIME_MODULES = ["chores", "groceries", "dinner", "todo", "games", "activities", "recipes", "calendar"] as const;
export type RealtimeModule = typeof REALTIME_MODULES[number];

export interface ChoresRealtimeEvent {
  resource: "chore";
  action: "created" | "updated" | "completed" | "deleted";
  entityId?: number;
}

export interface GroceriesRealtimeEvent {
  resource: "need" | "item" | "store";
  action: "created" | "updated" | "checked" | "reordered" | "cleared" | "deleted";
  entityId?: number;
}

export interface DinnerRealtimeEvent {
  resource: "meal";
  action: "created" | "updated" | "deleted" | "confirmed" | "unconfirmed" | "skipped" | "chosen";
  entityId?: number;
}

export interface TodoRealtimeEvent {
  resource: "todo";
  action: "created" | "updated" | "deleted" | "completed" | "reordered";
  entityId?: number;
}
export type ActivitiesRealtimeEvent = { resource: "activity"; action: "created" | "updated" | "deleted" | "completed" | "reordered"; entityId?: number };
export type GamesRealtimeEvent = { resource: "game"; action: "created" | "updated" | "deleted" | "confirmed" | "unconfirmed" | "chosen" | "skipped"; entityId?: number };
export interface RecipesRealtimeEvent { resource: "recipe"; action: "created" | "updated" | "finalized" | "deleted" | "image"; entityId?: number; }
export interface CalendarRealtimeEvent { resource: "event"; action: "created" | "updated" | "deleted"; entityId?: number; }

export type RealtimeModuleEvent =
  | { module: "chores"; event: ChoresRealtimeEvent }
  | { module: "groceries"; event: GroceriesRealtimeEvent }
  | { module: "dinner"; event: DinnerRealtimeEvent }
  | { module: "todo"; event: TodoRealtimeEvent }
  | { module: "games"; event: GamesRealtimeEvent }
  | { module: "activities"; event: ActivitiesRealtimeEvent }
  | { module: "recipes"; event: RecipesRealtimeEvent }
  | { module: "calendar"; event: CalendarRealtimeEvent };

export interface RealtimeEventEnvelope {
  version: typeof REALTIME_PROTOCOL_VERSION;
  type: "event";
  id: string;
  sequence: number;
  occurredAt: string;
  actorUserId: number;
  originClientId?: string;
  module: RealtimeModule;
  event: ChoresRealtimeEvent | GroceriesRealtimeEvent | DinnerRealtimeEvent | TodoRealtimeEvent | GamesRealtimeEvent | ActivitiesRealtimeEvent | RecipesRealtimeEvent | CalendarRealtimeEvent;
}

export type RealtimeClientMessage =
  | {
      version: typeof REALTIME_PROTOCOL_VERSION;
      type: "subscribe";
      modules: RealtimeModule[];
    }
  | {
      version: typeof REALTIME_PROTOCOL_VERSION;
      type: "ping";
      sentAt: number;
    };

export type RealtimeServerMessage =
  | {
      version: typeof REALTIME_PROTOCOL_VERSION;
      type: "ready";
      sequence: number;
    }
  | {
      version: typeof REALTIME_PROTOCOL_VERSION;
      type: "pong";
      sentAt: number;
    }
  | {
      version: typeof REALTIME_PROTOCOL_VERSION;
      type: "subscribed";
      modules: RealtimeModule[];
      sequence: number;
    }
  | {
      version: typeof REALTIME_PROTOCOL_VERSION;
      type: "error";
      message: string;
    }
  | RealtimeEventEnvelope;

const moduleNames = new Set<string>(REALTIME_MODULES);

export function isRealtimeModule(value: unknown): value is RealtimeModule {
  return typeof value === "string" && moduleNames.has(value);
}

export function parseRealtimeClientMessage(value: string): RealtimeClientMessage | null {
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    if (parsed.version !== REALTIME_PROTOCOL_VERSION) return null;
    if (parsed.type === "ping" && typeof parsed.sentAt === "number") {
      return { version: REALTIME_PROTOCOL_VERSION, type: "ping", sentAt: parsed.sentAt };
    }
    if (
      parsed.type === "subscribe" &&
      Array.isArray(parsed.modules) &&
      parsed.modules.length > 0 &&
      parsed.modules.every(isRealtimeModule)
    ) {
      return {
        version: REALTIME_PROTOCOL_VERSION,
        type: "subscribe",
        modules: [...new Set(parsed.modules)],
      };
    }
  } catch {}
  return null;
}

export function parseRealtimeServerMessage(value: string): RealtimeServerMessage | null {
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    if (parsed.version !== REALTIME_PROTOCOL_VERSION || typeof parsed.type !== "string") return null;
    if (parsed.type === "ready" && typeof parsed.sequence === "number") return parsed as unknown as RealtimeServerMessage;
    if (parsed.type === "pong" && typeof parsed.sentAt === "number") return parsed as unknown as RealtimeServerMessage;
    if (parsed.type === "subscribed" && Array.isArray(parsed.modules) && parsed.modules.every(isRealtimeModule) && typeof parsed.sequence === "number") return parsed as unknown as RealtimeServerMessage;
    if (parsed.type === "error" && typeof parsed.message === "string") return parsed as unknown as RealtimeServerMessage;
    if (
      parsed.type === "event" &&
      typeof parsed.id === "string" &&
      typeof parsed.sequence === "number" &&
      typeof parsed.occurredAt === "string" &&
      typeof parsed.actorUserId === "number" &&
      isRealtimeModule(parsed.module) &&
      parsed.event !== null &&
      typeof parsed.event === "object"
    ) {
      return parsed as unknown as RealtimeEventEnvelope;
    }
  } catch {}
  return null;
}
